import { MaskitoPreprocessor } from "@maskito/core";
import { SelectionRange } from "@maskito/core/src/lib/types";

/**
 * This is a custom-written preprocessor to handle the zero shortcut on slash.
 * e.g: 1/ -> 01/ and 01/1/ -> 01/01/
 *
 * @param templateString - A string representing the mask. e.g. "dd/mm/yyyy" or "__/__/____"
 * @param separator - The separator to use for segments of the date. Must match the separator in the template string.
 * @returns A MaskitoPreprocessor function.
 */
export const makeZeroShortcutPreprocessor: (
  templateString: string,
  separator: string,
) => MaskitoPreprocessor = (templateString, separator) => {
  const zeroShortcutPreprocessor: MaskitoPreprocessor = (
    { elementState, data },
    actionType,
  ) => {
    // Only process when user is inserting a separator character
    if (
      actionType === "insert" && // the user is inserting a character
      data === separator && // the user typed the separator character
      elementState.selection[0] === elementState.selection[1] // only entering a single character
    ) {
      const selectionIndex = elementState.selection[0];

      // Find all separator positions in the template AFTER the current cursor position
      // This helps us determine the boundaries of the current segment
      const separatorPositions: number[] = templateString
        .split("")
        .map((char, index) => (char === separator ? index : null))
        .filter((position) => position !== null)
        .filter((position) => position > selectionIndex);

      // Early exit conditions - don't apply zero padding in these cases
      const noRemainingSegments = !separatorPositions.length;
      const nothingEnteredYet = !elementState.value.length;
      const previousCharacterIsNotADigit = !/^\d$/.test(
        elementState.value.slice(-1),
      );
      const currentCharacterIsSeparator =
        templateString[selectionIndex] === separator;

      if (
        noRemainingSegments ||
        nothingEnteredYet ||
        previousCharacterIsNotADigit ||
        currentCharacterIsSeparator
      ) {
        return { elementState, data };
      }

      // Find the start of the current segment by looking for the last separator or space (for date ranges)
      // This determines where the current number segment begins
      const firstIndexOfSegment = Math.max(
        elementState.value.lastIndexOf(separator) + 1,
        elementState.value.lastIndexOf(" ") + 1,
        0,
      );

      // Find the end of the current segment (next separator position)
      const lastIndexOfSegment = separatorPositions[0];

      // Extract the digits currently entered in this segment
      const digitsCurrentlyInSegment =
        elementState.value.slice(firstIndexOfSegment);

      // Calculate how many digits this segment should have based on the template
      // e.g., if template is "dd/mm/yyyy", the first segment should have 2 digits
      const targetNumberOfDigitsInSegment = templateString.slice(
        firstIndexOfSegment,
        lastIndexOfSegment,
      ).length;

      // Pad the current segment with leading zeros to reach the target length
      // e.g., "1" becomes "01" for a 2-digit segment
      const newSegment = digitsCurrentlyInSegment.padStart(
        targetNumberOfDigitsInSegment,
        "0",
      );

      // Construct the new value by replacing the current segment with the padded version
      const newValue = `${elementState.value.slice(0, firstIndexOfSegment)}${newSegment}`;

      // Return the updated state with the padded segment and separator
      // Position cursor after the separator
      return {
        elementState: {
          value: newValue + data,
          selection: [newValue.length, newValue.length] as SelectionRange,
        },
        data,
      };
    }
    // If conditions aren't met, return unchanged state
    return { elementState, data };
  };
  return zeroShortcutPreprocessor;
};
